import type { ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
//eslint-disable-next-line @wyz/module/bad-module-import
import { ConnectQueryKeys } from '~/connect/api';
//eslint-disable-next-line @wyz/module/bad-module-import
import { COLLECTIONS } from '~/connect/schemas';
import { PurchaseQueryKeys } from '~/purchase/api';
import type {
  IDistributionPricingPolicyDetail,
  IQuoteErrorResponse,
  ISaveAdvancedExtraBonusesVariables,
  ISaveDistributionPricingPolicyBrandVariables,
} from '~/purchase/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IDistributionPricingPolicyDetail>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      customerGroupId: string;
    };
  };
  saveBrand?: UseMutationOptions<
    ISuccessApiResponse<IDistributionPricingPolicyDetail>,
    IQuoteErrorResponse,
    ISaveDistributionPricingPolicyBrandVariables
  >;
  saveAdvancedBonuses?: UseMutationOptions<
    ISuccessApiResponse<IDistributionPricingPolicyDetail>,
    IQuoteErrorResponse,
    ISaveAdvancedExtraBonusesVariables
  >;
};

export const useManageDistributionPricingPolicy = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.distributionPricingPolicies.detail(
      options.get?.args.customerGroupId as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IDistributionPricingPolicyDetail>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/distribution-pricing-policies/${options.get?.args.customerGroupId}`,
      ),
    refetchOnWindowFocus: false,
    ...options.get,
    enabled: Boolean(options.get?.args.customerGroupId) && options.get?.enabled,
  });

  const saveBrandMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IDistributionPricingPolicyDetail>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/distribution-pricing-policies/${variables.customerGroupId}/brands`,
        { method: 'PUT', body: JSON.stringify(variables.body) },
      ),
    // The save also flips the listing's Oui/Non flag and its "Modifié le" date.
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.distributionPricingPolicies.base(),
      ConnectQueryKeys.changeLogs.paginate(
        COLLECTIONS.DistributionPricingPolicyExtraBonus,
      ),
    ],
    retry: false,
    ...options.saveBrand,
  });

  const saveAdvancedBonusesMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IDistributionPricingPolicyDetail>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/distribution-pricing-policies/${variables.customerGroupId}/advanced-bonuses`,
        { method: 'PUT', body: JSON.stringify(variables.body) },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.distributionPricingPolicies.base(),
      ConnectQueryKeys.changeLogs.paginate(
        COLLECTIONS.DistributionPricingPolicyExtraBonus,
      ),
    ],
    retry: false,
    ...options.saveAdvancedBonuses,
  });

  return restrictApi(options, {
    get: getQuery,
    saveBrand: saveBrandMutation,
    saveAdvancedBonuses: saveAdvancedBonusesMutation,
  });
};
